'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import {
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { limitNumberOfPaginationPages } from '@//utils/pagination';

const transformQuery = (q?: string) => {
  const params = new URLSearchParams();
  const p = new URLSearchParams(q || '');
  if (p.get('offset')) {
    params.set('offset', p.get('offset') || '');
  }
  if (p.get('sort') && p.get('sort') !== 'publishDesc') {
    params.set('sort', p.get('sort') || '');
  }

  if (p.get('q')) {
    params.set('q', p.get('q') || '');
  }

  params.sort();

  return params.toString() ? `?${params}` : '';
};

interface PaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const Pagination = ({ rootUrl, pagination }: PaginationProps) => {
  const t = useTranslations('other.pagination');
  const prev = pagination?.previousPage?.query
    ? `${rootUrl}${transformQuery(pagination?.previousPage?.query)}`
    : undefined;

  const next = pagination?.nextPage?.query
    ? `${rootUrl}${transformQuery(pagination?.nextPage?.query)}`
    : undefined;

  const pages = useMemo(
    () => limitNumberOfPaginationPages(pagination?.pages || []),
    [pagination?.pages],
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const offset = urlParams.get('offset');

    if (offset) {
      const articlesList = document.getElementById('articles-list');
      if (articlesList) {
        articlesList.focus();
      }
    }
  }, []);

  if (!pagination) {
    return null;
  }

  return (
    <StyledPagination testId="articles-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink variant="prev" href={prev} testId="articles-pagination-prev" />
      {pages.map((page) => (
        <PaginationLink
          key={page.displayName}
          href={`${rootUrl}${transformQuery(page.query)}`}
          title={t('goTo.title', { page: page.displayName })}
          testId={`articles-pagination-page-${page.displayName}`}
          isActive={page.selected}
        >
          {page.displayName}
        </PaginationLink>
      ))}
      <PaginationNavLink variant="next" href={next} testId="articles-pagination-next" />
    </StyledPagination>
  );
};

export default Pagination;
